import { NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ModalDirective, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { filter, Subscription, take } from 'rxjs';
import { ExitQuestionReason, ExitQuestionService, ExitQuestionStep } from 'src/app/shared/services/exit-question.service';
import { mixpanelTrack } from 'src/app/shared/utils/mixpanel.util';

let nextId = 0;

/** Shown while the reasons load or when they fail, so the popup still has a title. */
const FALLBACK_TITLE = 'Before you go, what held you back?';

/**
 * The exit question popup, placed once in the app shell. The leave guard opens it through
 * ExitQuestionService when a member leaves an unfinished flow (deposit, KYC, loans, sales): the
 * step's title and prompt, its reasons as single-choice options and a free-text box.
 *
 * The answer is what the team needs, so there is no Skip., no close cross, and neither Esc nor a
 * backdrop click closes it: Send. is the way on. The one exception is a send that fails: then
 * "Leave without sending." appears, so a network error never traps the member on the page. A
 * rate-limit refusal (429) closes it straight away, with no message. Once it closes, the
 * navigation the member started goes ahead.
 *
 * A reload while it is open brings it back: the question is restored on the next start
 * (ExitQuestionService), with no browser "Leave site?" check in between.
 */
@Component({
    selector: 'app-exit-question',
    templateUrl: './exit-question.component.html',
    styleUrls: ['./exit-question.component.css'],
    imports: [NgIf, NgFor, ModalDirective]
})
export class ExitQuestionComponent implements AfterViewInit, OnDestroy {
    @ViewChild('modal', { static: true }) modal!: ModalDirective;
    @ViewChild('modal', { static: true, read: ElementRef }) modalEl!: ElementRef<HTMLElement>;
    @ViewChild('panel', { static: true }) panel!: ElementRef<HTMLElement>;

    private readonly exitQuestionService = inject(ExitQuestionService);
    private readonly toastr = inject(ToastrService);
    private readonly router = inject(Router);
    private restoreSubscription?: Subscription;
    private sessionSubscription?: Subscription;
    /** The popup was closed because the member was signed out, not by the member. */
    private endedBySignOut = false;

    readonly maxLength = 1000;
    readonly warnFrom = 950;
    private readonly uid = nextId++;
    readonly titleId = `exit-question-title-${this.uid}`;
    readonly textId = `exit-question-text-${this.uid}`;
    readonly counterId = `exit-question-counter-${this.uid}`;

    readonly modalConfig: ModalOptions = { animated: true, backdrop: 'static', ignoreBackdropClick: true, keyboard: false };

    step: ExitQuestionStep | null = null;
    title = FALLBACK_TITLE;
    reasons: ExitQuestionReason[] = [];
    prompt = '';
    placeholder = '';
    selectedReasonId: string | null = null;
    text = '';
    sending = false;
    sent = false;
    /** A send failed: offer "Leave without sending." so the member is never stuck. */
    sendFailed = false;

    private closed?: () => void;

    get canSend(): boolean {
        return !this.sending && (this.selectedReasonId !== null || this.text.trim().length > 0);
    }

    ngAfterViewInit(): void {
        // Straight under <body>, outside the app's scroll container and any transformed ancestor.
        document.body.appendChild(this.modalEl.nativeElement);
        this.exitQuestionService.registerOpener((step) => this.open(step));
        // A question left open by a reload comes back once the first page has rendered.
        this.restoreSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd), take(1))
            .subscribe(() => void this.exitQuestionService.restorePending());
        this.sessionSubscription = this.exitQuestionService.sessionEnded$.subscribe(() => {
            this.endedBySignOut = true;
            this.modal.hide();
        });
    }

    ngOnDestroy(): void {
        this.restoreSubscription?.unsubscribe();
        this.sessionSubscription?.unsubscribe();
        this.exitQuestionService.registerOpener(undefined);
        this.closed?.();
        this.modalEl.nativeElement.remove();
    }

    /** Shows the popup for a step; resolves once it has closed. */
    private open(step: ExitQuestionStep): Promise<void> {
        this.step = step;
        this.title = FALLBACK_TITLE;
        this.reasons = [];
        this.prompt = '';
        this.placeholder = '';
        this.selectedReasonId = null;
        this.text = '';
        this.sent = false;
        this.sending = false;
        this.sendFailed = false;
        this.endedBySignOut = false;
        this.track('Exit Question Opened', { step, trigger: 'leave' });
        this.exitQuestionService.getReasons().then((steps) => {
            if (this.step !== step) return;
            const stepReasons = steps?.[step];
            this.title = stepReasons?.title || FALLBACK_TITLE;
            this.reasons = stepReasons?.reasons ?? [];
            this.prompt = stepReasons?.prompt ?? '';
            this.placeholder = stepReasons?.placeholder ?? '';
        });
        return new Promise<void>((resolve) => {
            this.closed = resolve;
            this.modal.show();
        });
    }

    /** Focus the first option, or the text box when there are none, so keyboard users start inside. */
    onShown(): void {
        this.panel.nativeElement.querySelector<HTMLElement>('[role="radio"], textarea')?.focus();
    }

    dismiss(): void {
        this.modal.hide();
    }

    onHidden(): void {
        if (this.endedBySignOut) this.track('Exit Question Skipped', { step: this.step, reason: 'signed_out' });
        else if (!this.sent) this.track('Exit Question Dismissed', { step: this.step });
        const closed = this.closed;
        this.closed = undefined;
        closed?.();
    }

    toggleReason(reason: ExitQuestionReason): void {
        this.selectedReasonId = this.selectedReasonId === reason.id ? null : reason.id;
    }

    /** Roving tab stop: the selected option, or the first one when none is picked. */
    chipTabIndex(reason: ExitQuestionReason, index: number): number {
        if (this.selectedReasonId === null) return index === 0 ? 0 : -1;
        return this.selectedReasonId === reason.id ? 0 : -1;
    }

    onChipKeydown(event: KeyboardEvent, index: number): void {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const chips = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
        if (!chips?.length) return;
        chips[(index + step + chips.length) % chips.length].focus();
    }

    onText(event: Event): void {
        this.text = (event.target as HTMLTextAreaElement).value;
    }

    async send(): Promise<void> {
        if (!this.canSend || !this.step) return;
        const text = this.text.trim();
        const body = { step: this.step, reason_id: this.selectedReasonId, text: text || null };
        this.sending = true;
        try {
            await this.exitQuestionService.submit(body);
            this.sent = true;
            this.track('Exit Question Sent', { step: this.step, reason_id: body.reason_id, has_text: !!body.text });
            // The thanks screen stays until Okay., like the progress modal's success screen.
            setTimeout(() => this.panel.nativeElement.querySelector<HTMLElement>('.exit-question__action')?.focus());
        } catch (error: any) {
            const message = error?.error?.message || 'Could not send your answer. Please try again.';
            if (error?.status === 429) {
                // Only the app-wide rate limiter answers 429 now: let the member go, quietly.
                this.modal.hide();
                return;
            }
            // The answer stays in the form so the member can try again, or leave.
            this.sendFailed = true;
            this.toastr.error(message);
        } finally {
            this.sending = false;
        }
    }

    private track(event: string, properties: Record<string, unknown>): void {
        try {
            mixpanelTrack(event, properties);
        } catch {
            // no-op: analytics must not break user flows.
        }
    }
}
